import React from 'react';
import PropTypes from 'prop-types';

const getMetricCode = (label) => String(label || '').split(' ')[0].toUpperCase();
const GM_METRIC_CODES = ['SGM', 'AGM', 'EGM'];
const getMetricDate = (label) => {
  const text = String(label || '');
  const lowerText = text.toLowerCase();
  const onTokenIndex = lowerText.indexOf('(on ');
  if (onTokenIndex === -1) return null;

  const dateStartIndex = onTokenIndex + 4;
  const closeParenIndex = text.indexOf(')', dateStartIndex);
  if (closeParenIndex === -1) return null;

  const dateText = text.slice(dateStartIndex, closeParenIndex).trim();
  return dateText || null;
};

const hasMetricValue = (value) => (
  value !== null
  && value !== undefined
  && value !== ''
  && Number.isFinite(Number(value))
);
const hasRenderableMetricGraph = (value) => hasMetricValue(value) && Number(value) !== 0;
const clampPercent = (value) => Math.max(-100, Math.min(100, Number(value) || 0));
const formatPercent = (value) => (hasMetricValue(value) ? `${Number(value).toFixed(2)}%` : '-');
const HALF_CHART_HEIGHT_DIVISOR = 3;
const getBarHeightPercent = (value) => (
  hasMetricValue(value)
    ? Math.min(Math.abs(clampPercent(value)), 100) / HALF_CHART_HEIGHT_DIVISOR
    : 0
);
const getBadgeTone = (metricCode, value) => {
  if (!hasMetricValue(value)) return 'default';

  const numericValue = Number(value);

  // Apply threshold color coding to AGM and EGM only.
  // green >= 50, amber > 0 and < 50, red <= 0.
  if (metricCode === 'AGM' || metricCode === 'EGM') {
    if (numericValue >= 50) return 'green';
    if (numericValue > 0) return 'amber';
    return 'red';
  }

  return 'default';
};

const GmCard = ({ hasGmData, gmData = [], fallbackDate = null }) => {
  const renderableGmData = gmData.filter((item) => hasRenderableMetricGraph(item?.value));
  const gmDataByCode = new Map(gmData.map((item) => [getMetricCode(item?.label), item]));
  const metricSlots = GM_METRIC_CODES.map((metricCode) => ({
    metricCode,
    item: gmDataByCode.get(metricCode) || null,
  }));
  const shouldShowGraph = hasGmData && renderableGmData.length > 0;
  const asOnDate = (shouldShowGraph
    ? gmData.map((item) => getMetricDate(item?.label)).find(Boolean)
    : null) || fallbackDate;

  return (
    <div className={`card gm-card ${shouldShowGraph ? 'with-data' : 'no-data'}`}>
      <div className="card-header">
        <h3 className="card-title">GM%</h3>
        <span className="divider" />
      </div>
      <div className="card-content">
        {shouldShowGraph ? (
          <div className="gm-graph-area">
            <div className="gm-chart gm-chart-custom">
              <div className="gm-zero-line" />
              <div
                className="gm-columns"
                style={{ gridTemplateColumns: `repeat(${GM_METRIC_CODES.length}, minmax(0, 1fr))` }}
              >
                {metricSlots.map(({ metricCode: slotCode, item }) => {
                  if (!hasRenderableMetricGraph(item?.value)) {
                    return <div className="gm-column" key={slotCode} />;
                  }

                  const metricCode = getMetricCode(item?.label);
                  const rawMetricValue = item?.value;
                  const hasValue = hasMetricValue(rawMetricValue);
                  const metricValue = hasValue ? clampPercent(rawMetricValue) : null;
                  const isMaxPositiveValue = (metricValue ?? 0) >= 100;
                  const isMaxNegativeValue = (metricValue ?? 0) <= -100;
                  const baseBarHeightPercent = getBarHeightPercent(rawMetricValue);
                  const hasCappedMetricValue = (
                    isMaxPositiveValue || isMaxNegativeValue
                  );
                  const barHeightPercent = hasCappedMetricValue
                    ? Math.max(baseBarHeightPercent - 4, 0)
                    : baseBarHeightPercent;
                  const badgeTone = getBadgeTone(metricCode, rawMetricValue);
                  const bubbleStyle = (metricValue ?? 0) >= 0
                    ? {
                      top: `max(10px, calc(50% - ${barHeightPercent}% - 38px))`,
                    }
                    : { top: `min(calc(100% - 44px), calc(50% + ${barHeightPercent}% + 8px))` };

                  return (
                    <div className="gm-column" key={slotCode}>
                      <div
                        className={[
                          'gm-value-badge',
                          `gm-value-badge-${badgeTone}`,
                          (metricValue ?? 0) < 0 ? 'negative' : 'positive',
                        ].join(' ')}
                        style={bubbleStyle}
                      >
                        {formatPercent(rawMetricValue)}
                      </div>
                      <div
                        className={`gm-bar ${(metricValue ?? 0) < 0 ? 'negative' : 'positive'}`}
                        style={{
                          height: `${barHeightPercent}%`,
                          backgroundColor: item?.color || '#c8c2cd',
                          opacity: hasValue ? 1 : 0,
                        }}
                      />
                      <div className="gm-metric-label">{metricCode}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="gm-legend">
              {metricSlots.map(({ metricCode, item }) => {
                return (
                  <div className="legend-item" key={metricCode}>
                    <span className="legend-text">{hasRenderableMetricGraph(item?.value) ? metricCode : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="no-data-state">No GM% data</div>
        )}

        <div className="gm-footer">{asOnDate ? `As on ${asOnDate}` : '-'}</div>
      </div>
    </div>
  );
};

export default GmCard;

GmCard.propTypes = {
  hasGmData: PropTypes.bool.isRequired,
  gmData: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    color: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  fallbackDate: PropTypes.string,
};
